import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Sample blog data - in a real app, this would come from an API
  const sampleBlogs = useMemo(() => [
    {
      id: 1,
      title: "Understanding Technical Analysis: A Beginner's Guide",
      excerpt: "Learn the fundamentals of technical analysis and how to read charts effectively for better trading decisions. Decode the language of the market with this comprehensive beginner's guide.",
      content: "Technical analysis is a method of evaluating securities by analyzing statistics generated by market activity, such as past prices and volume. This comprehensive guide covers the basics of chart patterns, indicators, and trend analysis...",
      author: "TrendVector Team",
      date: "2024-01-15",
      category: "trading",
      readTime: "8 min read",
      image: "/assets/blog/technical-analysis.jpg"
    },
    {
      id: 2,
      title: "Market Sentiment Analysis: How News Affects Stock Prices",
      excerpt: "Discover how market sentiment and news events can significantly impact stock prices and trading strategies.",
      content: "Market sentiment plays a crucial role in determining stock prices. This article explores how news events, social media, and investor psychology influence market movements...",
      author: "Dr. Sarah Johnson",
      date: "2024-01-12",
      category: "analysis",
      readTime: "6 min read",
      image: "/assets/blog/market-sentiment.jpg"
    },
    {
      id: 3,
      title: "Risk Management Strategies for Long-term Investors",
      excerpt: "Essential risk management techniques that every long-term investor should know to protect their portfolio.",
      content: "Risk management is the cornerstone of successful investing. Learn about diversification, position sizing, and other critical strategies to protect your investments...",
      author: "Michael Chen",
      date: "2024-01-10",
      category: "investment",
      readTime: "10 min read",
      image: "/assets/blog/risk-management.jpg"
    },
    {
      id: 4,
      title: "AI in Stock Market Analysis: The Future is Here",
      excerpt: "Explore how artificial intelligence is revolutionizing stock market analysis and what it means for traders.",
      content: "Artificial intelligence is transforming the way we analyze markets. From machine learning algorithms to natural language processing, AI is providing new insights into market behavior...",
      author: "TrendVector AI Team",
      date: "2024-01-08",
      category: "technology",
      readTime: "7 min read",
      image: "/assets/blog/ai-analysis.jpg"
    },
    {
      id: 5,
      title: "Sector Rotation Strategies: Timing the Market Cycles",
      excerpt: "Learn how to identify and capitalize on sector rotation patterns for better portfolio performance.",
      content: "Sector rotation is a key strategy for timing market cycles. This guide explains how different sectors perform during various market phases and how to position your portfolio accordingly...",
      author: "Lisa Rodriguez",
      date: "2024-01-05",
      category: "strategy",
      readTime: "9 min read",
      image: "/assets/blog/sector-rotation.jpg"
    },
    {
      id: 6,
      title: "Building a Diversified Portfolio: Asset Allocation Guide",
      excerpt: "A comprehensive guide to building a well-diversified portfolio that balances risk and return.",
      content: "Diversification is essential for reducing risk in your investment portfolio. Learn about different asset classes, correlation analysis, and how to build a balanced portfolio...",
      author: "David Park",
      date: "2024-01-03",
      category: "investment",
      readTime: "12 min read",
      image: "/assets/blog/portfolio-diversification.jpg"
    }
  ], []);

  const categories = [
    { id: 'all', name: 'All Posts', count: sampleBlogs.length },
    { id: 'trading', name: 'Trading', count: sampleBlogs.filter(blog => blog.category === 'trading').length },
    { id: 'analysis', name: 'Analysis', count: sampleBlogs.filter(blog => blog.category === 'analysis').length },
    { id: 'investment', name: 'Investment', count: sampleBlogs.filter(blog => blog.category === 'investment').length },
    { id: 'technology', name: 'Technology', count: sampleBlogs.filter(blog => blog.category === 'technology').length },
    { id: 'strategy', name: 'Strategy', count: sampleBlogs.filter(blog => blog.category === 'strategy').length }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setBlogs(sampleBlogs);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [sampleBlogs]);

  const filteredBlogs = selectedCategory === 'all' 
    ? blogs 
    : blogs.filter(blog => blog.category === selectedCategory);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Trading & Investment Blog
              </h1>
              <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto">
                Stay informed with expert insights, market analysis, and trading strategies from our team of financial experts.
              </p>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading blog posts...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredBlogs.map((blog) => (
                  <article key={blog.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // hide broken image and show gradient fallback
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextSibling;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 items-center justify-center text-white text-center" style={{ display: 'none' }}>
                        <div>
                          <div className="text-4xl mb-2">📊</div>
                          <div className="text-sm opacity-90">Blog Post</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium capitalize">
                          {blog.category}
                        </span>
                        <span className="mx-2">•</span>
                        <span>{blog.readTime}</span>
                        <span className="mx-2">•</span>
                        <span>{formatDate(blog.date)}</span>
                      </div>
                      
                      <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                        {blog.title}
                      </h2>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {blog.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {blog.author.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <span className="ml-2 text-sm text-gray-600">{blog.author}</span>
                        </div>
                        
                        <Link
                          to={`/blog/${blog.id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                        >
                          Read More →
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
            
            {filteredBlogs.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Blog Posts Found</h3>
                <p className="text-gray-600">No blog posts available in this category. Check back later for new content.</p>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Signup */}
        <section className="bg-blue-600 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Stay Updated with Our Latest Insights
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Get the latest trading tips, market analysis, and investment strategies delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-blue-300 focus:outline-none"
              />
              <button className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-lg font-medium transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogsPage;
